import * as d3 from "d3";
import { formatNumberCH, sanitizeKey } from "../utils/format";

/**
 * D3 Chart Core Utilities
 *
 * Provides reusable D3 helpers for creating scales, drawing grids/axes,
 * rendering line and stacked bar charts, and managing tooltips.
 *
 * Key Conventions:
 * - metricSuffix: "__volume" for CHF amounts, "__count" for deal counts
 * - Integer tick heuristics: For count data with max ≤20, only show whole numbers
 * - Tooltip positioning: Centers above data point, clamps to chart bounds
 */

export const createTimeScale = (
  data: Array<{ year: number }>,
  width: number,
  padding: number = 0.1
) => {
  const years = Array.from(new Set(data.map((d) => d.year))).sort((a, b) => a - b);
  return d3.scaleBand<number>().domain(years).range([0, width]).padding(padding);
};

export const createLinearScale = (
  maxValue: number,
  height: number,
  domainPadding: number = 1.1
) => {
  return d3
    .scaleLinear()
    .domain([0, maxValue * domainPadding])
    .range([height, 0])
    .nice();
};

export const createScales = (
  data: Array<{ year: number }>,
  renderedWidth: number,
  height: number,
  margin: { top: number; right: number; bottom: number; left: number }
) => {
  const chartWidth = renderedWidth - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;
  const xScale = createTimeScale(data, chartWidth);
  return { chartWidth, chartHeight, xScale };
};

export const createYScale = (maxValue: number, chartHeight: number) => {
  return createLinearScale(maxValue, chartHeight);
};

export const drawGrid = (
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  chartWidth: number,
  chartHeight: number,
  xScale: d3.ScaleBand<number>,
  yScale: d3.ScaleLinear<number, number, never>,
  yTickValues?: number[] | null
) => {
  let yTicks = yTickValues ?? yScale.ticks();

  // For small integer ranges (≤20), enforce integer ticks to avoid 2.5, 3.75, etc.
  // This is especially important for count data where fractional values are meaningless
  const [, yMax] = yScale.domain();
  if (!yTickValues && yMax <= 20) {
    yTicks = yTicks.filter((tick) => Number.isInteger(tick) && tick >= 0);
    if (yTicks.length < 2) {
      yTicks = getIntegerTicks(Math.ceil(yMax));
    }
  }

  g.selectAll(".grid-y")
    .data(yTicks)
    .enter()
    .append("line")
    .attr("class", "grid-y")
    .attr("x1", 0)
    .attr("x2", chartWidth)
    .attr("y1", (d) => yScale(d))
    .attr("y2", (d) => yScale(d))
    .style("stroke", "#E2E8F0")
    .style("stroke-width", 0.5)
    .style("opacity", 0.5);

  const tickValues = xScale.domain();
  g.selectAll(".grid-x")
    .data(tickValues)
    .enter()
    .append("line")
    .attr("class", "grid-x")
    .attr("x1", (d) => (xScale(d) ?? 0) + xScale.bandwidth() / 2)
    .attr("x2", (d) => (xScale(d) ?? 0) + xScale.bandwidth() / 2)
    .attr("y1", 0)
    .attr("y2", chartHeight)
    .style("stroke", "#E2E8F0")
    .style("stroke-width", 0.5)
    .style("opacity", 0.5);
};

export const drawAxes = (
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  chartWidth: number,
  chartHeight: number,
  xScale: d3.ScaleBand<number>,
  yScale: d3.ScaleLinear<number, number, never>,
  isVolume: boolean,
  yTickValues?: number[] | null,
  isExpanded: boolean = false
) => {
  let yTicks = yTickValues ?? yScale.ticks();

  // Apply integer tick heuristics for count data with small ranges
  const [, yMax] = yScale.domain();
  if (!yTickValues && !isVolume && yMax <= 20) {
    yTicks = yTicks.filter((tick) => Number.isInteger(tick) && tick >= 0);
    if (yTicks.length < 2) {
      yTicks = getIntegerTicks(Math.ceil(yMax));
    }
  }

  const axisColor = "#4A5568";

  const xAxis = g
    .append("g")
    .attr("transform", `translate(0,${chartHeight})`)
    .call(d3.axisBottom(xScale).tickSizeOuter(0));

  // Rotate year labels 45° to prevent overlap
  xAxis
    .selectAll("text")
    .style("font-size", isExpanded ? "14px" : "12px")
    .style("fill", axisColor)
    .attr("transform", "rotate(-45)")
    .style("text-anchor", "end")
    .attr("dx", "-0.8em")
    .attr("dy", "0.15em");

  const yAxis = g.append("g").call(
    d3
      .axisLeft(yScale)
      .tickValues(yTicks as number[])
      .tickSizeOuter(0)
      .tickFormat((d: any) => formatNumberCH(Number(d), isVolume ? "auto" : 0) as any)
  );

  yAxis
    .selectAll("text")
    .style("font-size", isExpanded ? "14px" : "12px")
    .style("fill", axisColor);

  // Remove default D3 domain lines and draw custom crisp lines
  xAxis.select(".domain").remove();
  yAxis.select(".domain").remove();

  g.append("line")
    .attr("x1", 0.5)
    .attr("x2", 0.5)
    .attr("y1", 0)
    .attr("y2", chartHeight)
    .style("stroke", axisColor)
    .style("stroke-width", 1)
    .style("shape-rendering", "crispEdges");

  g.append("line")
    .attr("x1", 0)
    .attr("x2", chartWidth)
    .attr("y1", chartHeight + 0.5)
    .attr("y2", chartHeight + 0.5)
    .style("stroke", axisColor)
    .style("stroke-width", 1)
    .style("shape-rendering", "crispEdges");

  yAxis.selectAll(".tick line").style("stroke-linecap", "butt");
  xAxis.selectAll(".tick line").style("stroke-linecap", "butt");
};

/**
 * Draw line chart with interactive hover detection.
 *
 * Each category gets:
 * - A connecting line path
 * - Small 3px dots for visual markers
 * - Large 8px invisible hit-targets for easier hovering (especially on touch devices)
 */
export const drawLineChart = (
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  data: any[],
  categories: string[],
  xScale: d3.ScaleBand<number>,
  yScale: d3.ScaleLinear<number, number, never>,
  colorOf: (c: string) => string,
  metricSuffix: string,
  tooltipHandler: { show: (x: number, y: number, html: string) => void; hide: () => void },
  margin: { top: number; right: number; bottom: number; left: number },
  isVolume: boolean
) => {
  const line = d3
    .line<any>()
    .x((d) => (xScale(d.year) ?? 0) + xScale.bandwidth() / 2)
    .y((d) => yScale(d.value))
    .curve(d3.curveMonotoneX);

  categories.forEach((category) => {
    const categoryKey = sanitizeKey(category);
    const lineData = data.map((d) => ({
      year: d.year,
      value: d[`${categoryKey}${metricSuffix}`] || 0,
    }));

    g.append("path")
      .datum(lineData)
      .attr("fill", "none")
      .attr("stroke", colorOf(category))
      .attr("stroke-width", 2)
      .attr("d", line as any);

    // Small visible dots
    g.selectAll(`.dot-${categoryKey}`)
      .data(lineData)
      .enter()
      .append("circle")
      .attr("class", `dot-${categoryKey}`)
      .attr("cx", (d) => (xScale(d.year) ?? 0) + xScale.bandwidth() / 2)
      .attr("cy", (d) => yScale(d.value))
      .attr("r", 3)
      .attr("fill", colorOf(category))
      .style("cursor", "pointer")
      .on("mouseover", function (event: any, d: any) {
        const formattedValue = formatNumberCH(d.value, isVolume ? "auto" : 0);
        const xPos = margin.left + ((xScale(d.year) ?? 0) + xScale.bandwidth() / 2);
        const yPos = margin.top + yScale(d.value);
        tooltipHandler.show(
          xPos,
          yPos,
          `<strong>${category}</strong><br/>${d.year}: ${formattedValue}`
        );
      })
      .on("mouseout", () => tooltipHandler.hide());

    // Large invisible hit-targets (8px radius provides better UX than requiring exact pixel hover)
    g.selectAll(`.dot-hit-${categoryKey}`)
      .data(lineData)
      .enter()
      .append("circle")
      .attr("class", `dot-hit-${categoryKey}`)
      .attr("cx", (d) => (xScale(d.year) ?? 0) + xScale.bandwidth() / 2)
      .attr("cy", (d) => yScale(d.value))
      .attr("r", 8)
      .attr("fill", "transparent")
      .style("pointer-events", "all")
      .style("cursor", "pointer")
      .on("mouseover", function (event: any, d: any) {
        const formattedValue = formatNumberCH(d.value, isVolume ? "auto" : 0);
        const xPos = margin.left + ((xScale(d.year) ?? 0) + xScale.bandwidth() / 2);
        const yPos = margin.top + yScale(d.value);
        tooltipHandler.show(
          xPos,
          yPos,
          `<strong>${category}</strong><br/>${d.year}: ${formattedValue}`
        );
      })
      .on("mouseout", () => tooltipHandler.hide());
  });
};

/**
 * Draw stacked bar chart.
 *
 * Uses D3's stack layout to transform flat data into cumulative y-positions:
 * Input:  [{ year: 2024, Biotech__volume: 10, ICT__volume: 5 }]
 * Output: [[[0,10], [10,15]], ...] (cumulative ranges for each segment)
 *
 * Tooltip shows the segment value (d[1] - d[0]) rather than cumulative total.
 */
export const drawStackedBars = (
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  data: any[],
  categories: string[],
  xScale: d3.ScaleBand<number>,
  yScale: d3.ScaleLinear<number, number, never>,
  colorOf: (c: string) => string,
  suffix: string,
  tooltipHandler: { show: (x: number, y: number, html: string) => void; hide: () => void },
  margin: { top: number; right: number; bottom: number; left: number },
  isVolume: boolean
) => {
  const stack = d3.stack<any>().keys(categories.map((cat) => sanitizeKey(cat)));
  const stackedData = stack(
    data.map((d) => {
      const row: Record<string, number | string> = { year: d.year } as any;
      categories.forEach((cat) => {
        row[sanitizeKey(cat)] = d[`${sanitizeKey(cat)}${suffix}`] || 0;
      });
      return row as any;
    })
  );

  g.selectAll(".category-group")
    .data(stackedData)
    .enter()
    .append("g")
    .attr("class", "category-group")
    .attr("fill", (d, i) => colorOf(categories[i]))
    .selectAll("rect")
    .data((d) => d as any)
    .enter()
    .append("rect")
    .attr("x", (d: any) => xScale(d.data.year) as number)
    .attr("y", (d: any) => yScale(d[1]))
    .attr("height", (d: any) => yScale(d[0]) - yScale(d[1]))
    .attr("width", xScale.bandwidth())
    .style("cursor", "pointer")
    .on("mouseover", function (event: any, d: any) {
      const category = categories[(stackedData as any).findIndex((s: any) => s.includes(d))];
      const value = (d[1] as number) - (d[0] as number);
      const formattedValue = formatNumberCH(value, isVolume ? "auto" : 0);
      const xPos = margin.left + ((xScale(d.data.year) as number) + xScale.bandwidth() / 2);
      const yPos = margin.top + yScale(d[1]);
      tooltipHandler.show(
        xPos,
        yPos,
        `<strong>${category}</strong><br/>${d.data.year}: ${formattedValue}`
      );
    })
    .on("mouseout", () => tooltipHandler.hide());
};

/**
 * Create tooltip handler with smart positioning.
 *
 * Positioning logic:
 * - Centers tooltip above data point (transform: translateY(-120%))
 * - Clamps horizontally to chart bounds with 6px padding
 * - Adjusts for tooltip width to prevent overflow
 * - Supports compact one-line mode for short messages
 */
export const createTooltipHandler = (
  tooltip: d3.Selection<HTMLDivElement, unknown, null, undefined>,
  margin: { top: number; right: number; bottom: number; left: number },
  chartWidth: number
) => {
  return {
    show(xPx: number, yPx: number, html: string, oneLine: boolean = false) {
      tooltip
        .style("opacity", 1)
        .style("background", "#ffffff")
        .style("border", "1px solid #E2E8F0")
        .style("padding", "8px")
        .style("border-radius", "8px")
        .style("color", "#1F2937")
        .style("box-shadow", "0 6px 20px rgba(16,24,40,0.08)")
        .style("top", `${yPx}px`)
        .style("transform", "translate(0, -120%)")
        .html(html);

      const node = tooltip.node();
      const ttWidth = node ? (node.getBoundingClientRect().width as number) : 0;

      // Center tooltip horizontally, but clamp to chart bounds
      let left = xPx - ttWidth / 2;
      const minLeft = margin.left + 6;
      const maxLeft = margin.left + chartWidth - ttWidth - 6;

      left = Math.max(minLeft, Math.min(left, maxLeft));
      tooltip.style("left", `${left}px`);

      if (oneLine) tooltip.style("white-space", "nowrap");
      else tooltip.style("white-space", "normal");
    },
    hide() {
      tooltip.style("opacity", 0);
    },
  };
};

/**
 * Calculate maximum Y-axis value considering chart mode and totals.
 *
 * STACKED MODE (column):
 * - Uses D3 stack layout to find highest cumulative value across all categories
 * - If showTotal=true, compares against grand totals (__grandTotalVolume/Count)
 *   to ensure the total line doesn't exceed the Y-axis
 *
 * LINE MODE:
 * - Finds max value across individual categories
 * - If showTotal=true, includes total line in comparison
 *
 * Falls back to summing all metric keys if totals aren't pre-computed.
 */
export const calculateMaxValue = (
  data: any[],
  categories: string[],
  mode: string,
  showTotal: boolean,
  suffix: string,
  allCategories: string[] | null,
  isVolume: boolean
) => {
  if (mode === "column") {
    const stack = d3.stack<any>().keys(categories.map((cat) => sanitizeKey(cat)));
    const stackedData = stack(
      data.map((d) => {
        const row: Record<string, number | string> = { year: d.year } as any;
        categories.forEach((cat) => {
          row[sanitizeKey(cat)] = d[`${sanitizeKey(cat)}${suffix}`] || 0;
        });
        return row as any;
      })
    );
    const maxStack = d3.max(stackedData, (series: any) => d3.max(series, (d: any) => d[1])) as
      | number
      | undefined;

    if (!showTotal) return maxStack || 0;

    const totalFieldMax = d3.max(data, (d: any) => {
      const pre = isVolume
        ? (d.__grandTotalVolume ?? d.totalVolume)
        : (d.__grandTotalCount ?? d.totalCount);
      if (pre != null) return pre as number;
      const metricKeys = Object.keys(d).filter((k) => k.endsWith(suffix));
      if (metricKeys.length) return d3.sum(metricKeys.map((k) => (d[k] || 0) as number));
      return 0;
    }) as number | undefined;
    return Math.max(maxStack || 0, totalFieldMax || 0);
  }

  const maxCategory =
    (d3.max(data, (d: any) =>
      d3.max(categories, (cat) => (d[`${sanitizeKey(cat)}${suffix}`] || 0) as number)
    ) as number | undefined) || 0;

  if (!showTotal) return maxCategory || 0;

  const totalFieldMax = d3.max(data, (d: any) => {
    const pre = isVolume
      ? (d.__grandTotalVolume ?? d.totalVolume)
      : (d.__grandTotalCount ?? d.totalCount);
    if (pre != null) return pre as number;
    const metricKeys = Object.keys(d).filter((k) => k.endsWith(suffix));
    if (metricKeys.length) return d3.sum(metricKeys.map((k) => (d[k] || 0) as number));
    return 0;
  }) as number | undefined;

  return Math.max(maxCategory || 0, totalFieldMax || 0);
};

/**
 * Generate integer tick values for small ranges.
 *
 * Used when D3's default ticks would produce fractional values (2.5, 3.75)
 * for count data where only whole numbers make sense.
 *
 * Example: maxValue=10, maxTicks=6 → [0, 2, 4, 6, 8, 10]
 */
export const getIntegerTicks = (maxValue: number, maxTicks: number = 6) => {
  const ticks: number[] = [];
  const step = Math.max(1, Math.ceil(maxValue / maxTicks));
  for (let i = 0; i <= maxValue; i += step) {
    ticks.push(i);
  }
  return ticks;
};
